import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import { Context, Hooks, Platform } from "@xt/xtbase";
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { isLandingPathname } from '@/utils/landingRoute';
import { publicAssetUrl } from '@/utils/publicSvgUrl';
import { markPwaInstallPromptDismissed, shouldShowPwaInstallPrompt } from '@/utils/pwaInstallPrompt';
import styles from './PWAInstallPrompt.module.less';

const { XtContext } = Context;
const { useTranslation } = Hooks;

const NO_PWA_PROMPT_PARAM = 'noPwaPrompt';
const NO_PWA_PROMPT_SESSION_KEY = 'no_pwa_prompt';
const APP_ICON_VERSION = '6';
// 与 less 中退出动画时长保持一致（fadeOut/scaleOut 0.26s）
const CLOSE_ANIMATION_MS = 260;

const FEATURES = [
  { key: 'pwa.install.modal.feature1', icon: 'bolt' },
  { key: 'pwa.install.modal.feature2', icon: 'chart' },
  { key: 'pwa.install.modal.feature3', icon: 'shield' },
];

const FEATURE_ICONS = {
  bolt: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
};

const PWAInstallPrompt = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const [appState] = useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const userId = appState?.userInfo?.userId;
  const isLoggedIn = loginStatus === 1;
  const { canInstall, isInstalled } = usePWAInstall();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [closing, setClosing] = useState(false);
  const [remindLater, setRemindLater] = useState(false);
  const dialogRef = useRef(null);
  const closeTimerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has(NO_PWA_PROMPT_PARAM)) {
        sessionStorage.setItem(NO_PWA_PROMPT_SESSION_KEY, '1');
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 仅登录后弹每日下载引导；loginStatus 0=鉴权中、2=未登录均不弹
    if (!isLoggedIn || userId == null || userId === "") {
      setShowInstallPrompt(false);
      return;
    }

    if (!shouldShowPwaInstallPrompt(userId)) {
      setShowInstallPrompt(false);
      return;
    }

    const path = window.location.pathname;
    if (isLandingPathname(path)) {
      setShowInstallPrompt(false);
      return;
    }

    if (canInstall && !isInstalled) {
      setShowInstallPrompt(true);
    } else {
      setShowInstallPrompt(false);
    }
  }, [canInstall, isInstalled, isLoggedIn, userId, router.asPath]);

  // 卸载时清理关闭动画定时器
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // 不锁 document.body 滚动：App WebView 下会与全局组件叠加导致整页无法滑动；
  // 弹层本身为 fixed overlay + dialog overflow-y:auto，足够阻止背景交互。

  // 打开时聚焦对话框，关闭时还原焦点
  useEffect(() => {
    if (!showInstallPrompt) return;
    previouslyFocusedRef.current = document.activeElement;
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [showInstallPrompt]);

  // Esc 关闭
  useEffect(() => {
    if (!showInstallPrompt) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        dismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInstallPrompt, closing]);

  // 关闭：先播退出动画再卸载；勾选"近日不再提醒"则按周（7 天）记录
  const dismiss = useCallback(() => {
    if (closing) return;
    setClosing(true);
    markPwaInstallPromptDismissed(userId, remindLater);
    closeTimerRef.current = setTimeout(() => {
      setShowInstallPrompt(false);
      setClosing(false);
    }, CLOSE_ANIMATION_MS);
  }, [closing, remindLater, userId]);

  const handleInstallClick = () => {
    setShowInstallPrompt(false);
    markPwaInstallPromptDismissed(userId, remindLater);
    // 引导下载：统一跳转 /qr，由服务端按 UA 重定向到 App Store / Google Play
    router.push('/qr');
  };

  if (typeof window !== 'undefined') {
    if (Platform.isInApp()) return null;
    if (sessionStorage.getItem(NO_PWA_PROMPT_SESSION_KEY)) return null;
    if (new URLSearchParams(window.location.search).has(NO_PWA_PROMPT_PARAM)) return null;
    if (isLandingPathname(window.location.pathname)) return null;
  }

  if (!isLoggedIn || isInstalled || !showInstallPrompt) {
    return null;
  }

  const appIconSrc = publicAssetUrl(`/icons/icon-192x192.png?v=${APP_ICON_VERSION}`);

  return (
    <div
      className={closing ? `${styles.overlay} ${styles.closing}` : styles.overlay}
      onClick={dismiss}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-install-modal-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.topAccent} aria-hidden />
        <button
          type="button"
          className={styles.closeBtn}
          onClick={dismiss}
          aria-label={t('pwa.install.modal.close') || 'Close'}
        >
          ×
        </button>

        <div className={styles.heroBg}>
          <div className={styles.badge}>
            <span className={styles.badgeDot} aria-hidden />
            {t('pwa.install.modal.badge') || 'Official Spikex App'}
          </div>

          <div className={styles.hero}>
            <div className={styles.iconWrap}>
              <div className={styles.iconRing} aria-hidden />
              <img
                className={styles.appIcon}
                src={appIconSrc}
                alt=""
                width={80}
                height={80}
              />
            </div>
            <h2 id="pwa-install-modal-title" className={styles.title}>
              {t('pwa.install.modal.title') || 'Get the Spikex App'}
            </h2>
            <p className={styles.subtitle}>
              {t('pwa.install.modal.subtitle') || 'Download the App for a better experience. Trade anywhere, anytime.'}
            </p>
          </div>
        </div>

        <div className={styles.body}>
          <ul className={styles.features}>
            {FEATURES.map(({ key, icon }) => (
              <li key={key} className={styles.feature}>
                <span className={styles.featureIcon} aria-hidden>
                  {FEATURE_ICONS[icon]}
                </span>
                <span className={styles.featureText}>{t(key)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.downloadBtn} onClick={handleInstallClick}>
            <svg
              className={styles.downloadIcon}
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v11" />
              <path d="m7 10 5 5 5-5" />
              <path d="M5 20h14" />
            </svg>
            {t('pwa.install.modal.button') || 'Download the App'}
          </button>
          <button type="button" className={styles.laterBtn} onClick={dismiss}>
            {t('pwa.install.cancel') || 'Later'}
          </button>
        </div>

        <label className={styles.remindLater}>
          <input
            type="checkbox"
            className={styles.remindCheckbox}
            checked={remindLater}
            onChange={(e) => setRemindLater(e.target.checked)}
          />
          <span className={styles.remindCheckboxBox} aria-hidden />
          <span className={styles.remindCheckboxText}>
            {t('pwa.install.modal.remindLater') || "Don't remind me this week"}
          </span>
        </label>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
